import Toast from "./toast";

/**
 * @returns {Object} params
 */

export function getQueryParams() {
    function transformToObject(prmstr) {
        const params = {};
        const prmarr = prmstr.split('&');
        for (let i = 0; i < prmarr.length; i++) {
            const tmparr = prmarr[i].split('=');
            params[tmparr[0]] = tmparr[1];
        }
        return params;
    }

    const prmstr = window.location.search.substring(1);
    return prmstr != null && prmstr != '' ? transformToObject(prmstr) : {};
}

/**
 * Gets the active currency code from the DOM
 * @returns {string|null} The currency code (e.g., "USD", "EUR") or null if not found
 */
export function getCurrencyCode() {
    const mainContent = document.querySelector('#main-content[data-currency-code]');
    return mainContent ? mainContent.getAttribute('data-currency-code') : null;
}

/**
 *
 * @param f Function that will be executed, must return true (if no retries needed) or false.
 * @param timeoutMs Maximum time that **timeoutMs** will wait
 * @param retryMs Retry f() every **retryMs** ms
 * @returns
 */

export const sleepRetry = (f, timeoutMs, retryMs) => {
    return new Promise((resolve, reject) => {
        const timeWas = new Date();
        const wait = setInterval(function () {
            if (f()) {
                clearInterval(wait);
                resolve();
            } else if (new Date() - timeWas > timeoutMs) {
                // Timeout
                clearInterval(wait);
                reject();
            }
        }, retryMs);
    });
};


export const flashElement = (element) => {
    element.classList.add('flash-effect');
    setTimeout(() => element.classList.remove('flash-effect'), 500);
};

export const showElem = (container) => {
    return container.classList.remove('os-hidden');
};

export const hideElem = (container) => {
    return container.classList.add('os-hidden');
};

export const addLoader = (container, msg = '') => {
    return (container.innerHTML = "<div class='os-loading-overlay'></div>" + msg);
};

/**
 * Check if project is on test or production mode and return relative or original url redirect, respectively
 *
 * @param {*} url Url to parse
 * @returns {string} Final url
 */
export const getRedirectUrl = (url) => {
    if (process.env.NODE_ENV === 'production') {
        return url;
    } else {
        if (url.startsWith('/')) return url;
        
        const relative = new URL(url);

        return relative.pathname + relative.search;
    }
};

export async function recalculateCart(app, quotedCartId, quotedCartVersion) {
    const {data, status, message} = await app.service.recalculateCart(quotedCartId, quotedCartVersion);
    if (status === 0) {
        app.session.set({
            sessionDetails: {
                quotedCartId: data.cart_id,
                quotedCartVersion: data.cart_version,
                currentCartId: data.cart_id,
                currentCartVersion: data.cart_version,
            }
        });
        if (data.message) {
            new Toast({
                url: data.link,
                message: data.message,
                type: 'success',
                duration: 5000,
            }).show();
        }
    } else {
        new Toast({
            message,
            type: 'error',
            duration: 5000,
        }).show();
    }
}

/**
 * Executes a callback if the `os_download_quote` query parameter exists.
 *
 * @param {(quoteId: string, buildUrl: (base: string) => string) => void} fn
 *   A callback function that receives:
 *   - quoteId: the value of the `os_download_quote` query parameter
 *   - buildUrl: a function that takes a base URL and returns the full quote download URL
 */
export const onOsDownloadQuote = (fn) => {
  const queryParams = getQueryParams();

  if (queryParams['os_download_quote']) {
    return fn(
      queryParams['os_download_quote'],
      (base) => `${base}/os/quote/${queryParams['os_download_quote']}/download`
    );
  }
};

/**
 * Executes a callback if the `os_product` query parameter exists.
 *
 * @param {(tcc: string) => void} fn
 *   A callback function that receives:
 *   - value: the value of the `os_product` query parameter
 */
export const onOsProductPage = (fn) => {
  const queryParams = getQueryParams();

  if (queryParams['os_product']) {
    return fn(
      queryParams['os_product'],
    );
  }
};

export const getLangFile = async (lang) => {
    try {
        const currentPath = __webpack_public_path__;

        const res = await fetch(`${currentPath}/locales/${lang}.json`);
        return res.json();
    } catch (e) {
        console.log('Unable to load translation file ', e);
    }
}

/**
 * Creates a typewriter effect by displaying text word by word (similar to ChatGPT/Grok)
 * @param {string} text - The text to display with typewriter effect
 * @param {function(string): void} setter - Function to set the current text (e.g., element.innerHTML = text or editor.setHTML(text))
 * @param {number} duration - Total duration in milliseconds for the entire animation (default: 500)
 * @returns {Promise<void>} - Resolves when the animation is complete
 * 
 * @example
 * // For rich text editor
 * await typewriterEffect('Hello World', (text) => editor.setHTML(text));
 * 
 * @example
 * // For DOM element
 * await typewriterEffect('Hello World', (text) => element.innerHTML = text);
 */
export function typewriterEffect(text, setter, duration = 500) {
    return new Promise((resolve) => {
        if (!text || !setter || typeof setter !== 'function') {
            resolve();
            return;
        }

        // Split by words (preserving HTML tags and whitespace)
        const words = text.match(/(<[^>]+>|\S+|\s+)/g) || [];
        const delay = duration / words.length;
        let currentText = '';
        let index = 0;

        const typeWord = () => {
            if (index < words.length) {
                currentText += words[index];
                setter(currentText);
                index++;
                setTimeout(typeWord, delay);
            } else {
                resolve();
            }
        };

        typeWord();
    });
}
